import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  LocationCity as LocationCityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { UserProfile } from '../types/auth';
import { Address, ResidenceSummary } from '../types/residence';

export const ResidenceStatsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ResidenceSummary | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. addresses 컬렉션에서 거주지 정보 조회
        const addressesSnapshot = await getDocs(collection(db, 'addresses'));
        const addressesData = addressesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Address[];
        setAddresses(addressesData);

        // 2. user_profiles 컬렉션에서 모든 사용자 조회
        const userProfilesSnapshot = await getDocs(collection(db, 'user_profiles'));
        const userProfiles = userProfilesSnapshot.docs.map(doc => doc.data()) as UserProfile[];

        // 3. 통계 계산
        const totalUsers = userProfiles.length;
        const usersWithResidence = userProfiles.filter(
          user => user.province_name && user.district_name
        ).length;
        const inputRate = totalUsers > 0 ? (usersWithResidence / totalUsers) * 100 : 0;

        // 4. 시/도별 통계
        const provinceCount: { [key: string]: number } = {};
        const districtCount: { [key: string]: { [key: string]: number } } = {};

        userProfiles.forEach(user => {
          if (user.province_name && user.district_name) {
            const province = user.province_name;
            const district = user.district_name;

            // 시/도 카운트
            provinceCount[province] = (provinceCount[province] || 0) + 1;

            // 구/군 카운트
            if (!districtCount[province]) {
              districtCount[province] = {};
            }
            districtCount[province][district] = (districtCount[province][district] || 0) + 1;
          }
        });

        // 5. 부산/창원/기타 합계
        const busan = provinceCount['부산광역시'] || 0;
        const changwon = provinceCount['창원시'] || 0;
        const others = Object.entries(provinceCount)
          .filter(([key]) => key !== '부산광역시' && key !== '창원시')
          .reduce((sum, [, count]) => sum + count, 0);

        // 6. 상세 통계 생성
        const detailedStats = Object.entries(districtCount).flatMap(([province, districts]) =>
          Object.entries(districts).map(([district, count]) => ({
            provinceName: province,
            districtName: district,
            count,
          }))
        );

        // 정렬: 부산 > 창원 > 기타 순, 같은 시내에서는 카운트 내림차순
        detailedStats.sort((a, b) => {
          const provinceOrder = (p: string) => {
            if (p === '부산광역시') return 0;
            if (p === '창원시') return 1;
            return 2;
          };

          const orderA = provinceOrder(a.provinceName);
          const orderB = provinceOrder(b.provinceName);

          if (orderA !== orderB) return orderA - orderB;
          return b.count - a.count;
        });

        setSummary({
          totalUsers,
          usersWithResidence,
          inputRate,
          provinceStats: { busan, changwon, others },
          detailedStats,
        });
      } catch (error) {
        console.error('Error fetching residence stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!summary) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Typography color="error">데이터를 불러올 수 없습니다.</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          거주지 통계
        </Typography>

        {/* 요약 카드 */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ mr: 1, color: '#667eea' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    전체 사용자
                  </Typography>
                </Box>
                <Typography variant="h4">{summary.totalUsers.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, color: '#10b981' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    거주지 입력 완료
                  </Typography>
                </Box>
                <Typography variant="h4">{summary.usersWithResidence.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationCityIcon sx={{ mr: 1, color: '#f59e0b' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    입력률
                  </Typography>
                </Box>
                <Typography variant="h4">{summary.inputRate.toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 시/도별 합계 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              시/도별 합계
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <Paper sx={{ p: 2, bgcolor: '#eff6ff' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    부산광역시
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {summary.provinceStats.busan.toLocaleString()}명
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <Paper sx={{ p: 2, bgcolor: '#f0fdf4' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    창원시
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {summary.provinceStats.changwon.toLocaleString()}명
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <Paper sx={{ p: 2, bgcolor: '#fef3c7' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    그 외
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {summary.provinceStats.others.toLocaleString()}명
                  </Typography>
                </Paper>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 상세 통계 테이블 */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              거주지별 상세 통계
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell>시/도</TableCell>
                    <TableCell>구/군</TableCell>
                    <TableCell align="right">사용자 수</TableCell>
                    <TableCell align="right">비율</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.detailedStats.map((stat, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{stat.provinceName}</TableCell>
                      <TableCell>{stat.districtName}</TableCell>
                      <TableCell align="right">{stat.count.toLocaleString()}명</TableCell>
                      <TableCell align="right">
                        {((stat.count / summary.usersWithResidence) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  {summary.detailedStats.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">데이터가 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};
